/** @format */
import React from "react";
import {
  Box,
  Card,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const useStyles = (theme, isMobile) => ({
  container: {
    with: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  fullCardContainer: {
    width: "fit-content",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    margin: "5rem 0 9rem 0",
  },
  titleContainer: {
    blue: {
      width: "14rem",
      padding: ".4rem",
      textAlign: "center",
      border: ".1rem solid #e7f0fc",
      borderBottom: "none",
      borderTopLeftRadius: "1.25rem",
      borderTopRightRadius: "1.25rem",
      boxShadow: "0 .25rem .5rem #e7f0fc",
      background:
        "linear-gradient(90deg, rgba(249,252,253,1) 0%, rgba(243,249,252,1) 100%)",
    },
    yellow: {
      width: "14rem",
      display: "flex",
      justifyContent: "center",
      textAlign: "center",
      padding: ".4rem",
      border: ".1rem solid #fff2c7",
      borderBottom: "none",
      borderTopLeftRadius: "1.25rem",
      borderTopRightRadius: "1.25rem",
      background:
        "linear-gradient(900deg, rgba(255,254,249,1) 0%, rgba(255,251,238,1) 100%)",
      boxShadow: "0 .25rem .5rem #fff2c7",
    },
    red: {
      width: "14rem",
      padding: ".4rem",
      textAlign: "center",
      border: ".1rem solid #ffcaca",
      borderBottom: "none",
      borderTopLeftRadius: "1.25rem",
      borderTopRightRadius: "1.25rem",
      boxShadow: "0 .25rem .5rem #ffcaca",
      background:
        "linear-gradient(90deg, rgba(255,252,252,1) 0%, rgba(255,246,246,1) 100%)",
    },
  },
  title: {
    blue: {
      fontFamily: theme.typography.montserrat.semibold,
      textAlign: "center",
      color: "#75bbe1",
      fontSize: "1.3rem",
      padding: "0.4rem",
    },
    yellow: {
      display: "flex",
      justifyContent: "center",
      color: "#ffb800",
      textAlign: "center",
      fontSize: "1.3rem",
      padding: "0.4rem",
      fontFamily: theme.typography.montserrat.semibold,
    },
    red: {
      fontSize: "1.3rem",
      padding: "0.4rem",
      color: "#f82747",
      fontFamily: theme.typography.montserrat.semibold,
      textAlign: "center",
    },
  },
  subTitle: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "1.3rem",
    fontFamily: theme.typography.montserrat.semibold,
    color: theme.palette.blue.main,
    margin: "1.3rem 0 1rem 0",
  },
  subSubTitle: {
    textAlign: "center",
    fontWeight: "lighter",
    fontStyle: "italic",
    fontSize: ".96rem",
    fontFamily: theme.typography.montserrat.light,
    color: "#606060",
    margin: "1.3rem 0 1rem 0",
  },
  stage: {
    textAlign: "center",
    fontWeight: "lighter",
    fontStyle: "italic",
    fontSize: ".96rem",
    fontFamily: theme.typography.montserrat.light,
    color: "#606060",
    margin: "1.3rem 0 1rem 0",
  },
  divider: {
    margin: "0 0 .7rem 0",
  },
  card: {
    blue: {
      width: isMobile ? "17rem" : "22rem",
      minHeight: "33rem",
      padding: "1rem",
      border: "0.1rem solid #e7f0fc",
      borderRadius: "1rem",
      boxShadow: "0 .25rem 3rem #e7f0fc",
      background:
        "linear-gradient(185deg, rgba(249,252,253,1) 90%, rgba(243,249,252,1) 100%)",
    },
    yellow: {
      width: isMobile ? "17rem" : "22rem",
      minHeight: "33rem",
      padding: "1rem",
      border: ".1rem solid #fff2c7",
      borderRadius: "2.3rem",
      boxShadow: "0 .25rem .5rem #fff2c7",
      background:
        "linear-gradient(185deg, rgba(255,255,255,1) 90%, rgba(255,252,244,1) 100%)",
    },
    red: {
      width: isMobile ? "17rem" : "22rem",
      minHeight: "33rem",
      padding: "1rem",
      border: "0.1rem solid #ffcaca",
      borderRadius: "1rem",
      boxShadow: "0 .25rem .8rem #ffcaca",
      background:
        "linear-gradient(185deg, rgba(255,252,252,1) 90%, rgba(255,243,243,1) 100%);",
    },
  },
  list: {
    marginLeft: "1rem",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  item: {
    display: "flex",
    alignItems: "center",
  },
  itemText: {
    fontFamily: theme.typography.anaHeim.regular,
    color: theme.palette.blue.main,
    fontSize: "1.1rem",
  },
  itemIcon: {
    blue: {
      color: "rgb(126,206,250)",
    },
    yellow: {
      color: "rgb(251,210,112)",
    },
    red: {
      color: "rgb(255,6,46)",
    },
  },
});

const SyllabusCard = ({ units, stage, period, title, lessons, color }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:1230px)");
  const classes = useStyles(theme, isMobile);

  return (
    <Box sx={classes.container}>
      <Box sx={classes.fullCardContainer}>
        <Box>
          <Box style={classes.titleContainer[color]}>
            <Typography sx={classes.title[color]}>{units}</Typography>
          </Box>
        </Box>
        <Card sx={classes.card[color]}>
          <div>
            <Typography sx={classes.stage}>{stage}</Typography>
            <Typography sx={classes.subTitle}>{title}</Typography>
            <Typography sx={classes.subSubTitle}>{period}</Typography>
          </div>

          <Divider sx={classes.divider}></Divider>

          <div>
            <List sx={classes.list}>
              {lessons.map((lesson) => (
                <ListItem sx={classes.item} key={uuidv4()}>
                  <ListItemIcon>
                    <CheckCircleRoundedIcon sx={classes.itemIcon[color]} />
                  </ListItemIcon>
                  <ListItemText
                    primary={lesson.topic}
                    primaryTypographyProps={classes.itemText}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        </Card>
      </Box>
    </Box>
  );
};

export default SyllabusCard;
